import { User } from '../interfaces/user.interface';
import { Test } from './test.interface';
import { ServerFilter } from '../components/FilterBox/FilterBox';
import { PageType } from './bulk.interface';

export type AdminUserTestResponse = {
  metadata: Metadata;
  data: Datum[];
};

type Datum = {
  tests: number;
  testSuiteId: string;
  server: string;
};

type Metadata = {
  total: number;
  page: number;
  limit: number;
  nameAndSurname: string;
  company: string;
  location: string;
};

export type AdminUserTestsQuery = {
  id: string;
  params: {
    server?: string;
    testSuiteId?: string;
    status?: string;
    date?: string;
  };
};

export type AdminUserTestsFiltersResponse = {
  filters: AdminUserTestsFilter[];
};

type AdminUserTestsFilter = {
  name: string;
  value: string;
  filter: Array<{
    value: string;
    label: string;
  }>;
};

export type IGroup = {
  _id: string;
  name: string;
  description: string;
  image: string;
  users: User[];
  tests: Test[];
  admins: User[];
};

export type AdminGroupsQuery = {
  page: number;
  limit: number;
};

export type AdminGroup = {
  _id: string;
  name: string;
  description: string;
  image?: string;
  pageTypes: PageType[];
  usersCount: number;
  testsCount: number;
};

export type AdminGroupsResponse = {
  result?: AdminGroup[];
  count?: number;
};

export type AdminGroupFilter = {
  value: string;
  label: string;
};
export type AdminGroupsListResponse = Array<AdminGroupFilter>;

export type AdminFiltersResponse = { filters: ServerFilter[] };

export type AdminUsersPayload = {
  page: number;
  limit: number;
  name?: string;
  location?: string;
  company?: string;
  jobTitle?: string;
  email?: string;
  isBlocked?: string;
  isDeactivated?: string;
};

export type AdminUserEmailsPayload = Omit<AdminUsersPayload, 'page' | 'limit'>;

export type AdminUserEmailsResponse = {
  emails: string[];
};

export type AdminUsersResponse = {
  paginatedResults: User[];
  totalCount: number;
};

export type BillingsPayload = {
  page: number;
  limit: number;
};

type Billing = {
  groupID: string;
  groupName: string;
  count: number;
  totalPrice: number;
};

type MetaData = {
  total: number;
  page: number;
  limit: number;
};

export type BillingResponse = {
  data: Billing[];
  metaData: MetaData;
};

export type BillingsGroupPayload = {
  id: string;
  dateFrom: string;
  dateTo: string;
};

export type BillingGroup = {
  testSuiteId: string;
  server: string;
  totalCount: number;
  failedCount: number;
  finishedCount: number;
};

export type BillingsGroupResponse = {
  data: BillingGroup[];
  name: string;
};

export type AdminGroupsStatsDatesPayload = {
  group?: string;
};

export type AdminGroupsStatsQuery = {
  group: string;
  dateFrom: string;
  dateTo: string;
  status: string;
};

export type AdminGroupsStatsDateFilter = {
  year: { label: string; value: string };
  months: { label: string; value: string }[];
};

export type AdminGroupsStatsDatesResponse = Array<AdminGroupsStatsDateFilter>;

export type AdminGroupsStats = {
  count: number;
  testSuiteId: string;
  dateOfExecution: string; // from HistoryReport
};

export type AdminGroupsStatsResponse = Array<AdminGroupsStats>;

export type IYear = {
  label: string;
  value: string;
};

export type IMonth = {
  label: string;
  value: string;
};

export type StatsResponse = {
  year: IYear;
  months: IMonth[];
};

export type CreateAdminUserPayload = {
  nameAndSurname: string;
  nickName: string;
  email: string;
  company: string;
  groupId?: string;
  jobTitle: string;
  department: string;
  location?: string;
  role?: string;
};

export type AdminCreateGroupPayload = {
  name: string;
  description: string;
};

export type GetGroupStatsPayload = {
  group: string;
  dateFrom: string;
  dateTo: string;
  status: string;
};

export type GetGroupStatsResponse = Array<AdminGroupsStats>;

export type AdminUpdateUserPayload = {
  id: string;
  data: {
    nameAndSurname: string;
    nickName: string;
    email: string;
    company: string;
    jobTitle: string;
    department: string;
    location?: string;
    role?: string;
  };
};

export enum AdminGroupFilters {
  EMAIL = 'email'
}

export enum AdminGroupTestsFilters {
  REQUESTER = 'requester',
  TEST_SUITE_ID = 'testSuiteId',
  DATE = 'date'
}

export type AdminGroupsFiltersByIdPayload = {
  id: string;
  filters?: AdminGroupFilters[];
};

export type SetAdminsPayload = {
  groupId: string;
  users: string[];
};

export type RemoveUsersGroupPayload = {
  groupId: string;
  users: string[];
};

export type RemoveAdminGroupsPayload = {
  ids: string[];
};

export type GetAdminGroupByIdPayload = {
  id: string;
};

export type GetAdminGroupByIdResponse = {
  name: string;
  description: string;
};

export type GetAdminGroupUsersPayload = {
  id: string;
  page: number;
  limit: number;
  emails?: string;
  isBlocked?: string;
};
export type GetAdminGroupUsersResponse = {
  users: User[];
  admins: User[];
  userCount: number;
};

export type GetAdminGroupTestsPayload = {
  id: string;
  page: number;
  limit: number;
  dateRange: string;
  requesters?: string;
  testTypes?: string;
};

export type GetAdminGroupTestsResponse = {
  tests: Test[];
  testCount: number;
};

export enum AdminGroupTabs {
  USERS = 0,
  TESTS = 1,
  GROUP_STATISTICS = 2
}

export type SuccessMessageResponse = {
  message: string;
};

export type AdminEditGroupPayload = {
  id: string;
  data: GroupData;
};

export type GroupData = {
  name: string;
  description: string;
  pageTypes: PageType[];
  runTask: boolean;
  adminExtractData: boolean;
  modifyEmbargoKeyword: boolean;
  modifyUrlExceptions: boolean;
  modifySkuChecker: boolean;
};

export type GetGroupDetailsByIdPayload = {
  _id: string;
  name: string;
  description: string;
  pageTypes: PageType[];
  runTask?: boolean;
  adminExtractData?: boolean;
  modifyEmbargoKeyword?: boolean;
  modifyUrlExceptions?: boolean;
  modifySkuChecker?: boolean;
};

export type ResetGropUsersPasswordsPayload = {
  groupId: string;
  users: string[];
};

export type AddGroupUsersPayload = {
  groupId: string;
  users: string[];
};
